import {
  createAsyncThunk,
  createEntityAdapter,
  createSelector,
  createSlice,
} from '@reduxjs/toolkit';
import _ from 'lodash';
import {
  deleteImageFromS3,
  listImages,
  uploadImageToS3,
} from '../services/s3-service';
import env from './../../../../../common/config/env';
const productImagesAdaptor = createEntityAdapter({
  selectId: (imageObj) => imageObj.folder,
});

const initialState = productImagesAdaptor.getInitialState();
/**
 * Creates a slice for image folder entities
 * Folder entity will have the following structure:
 * {
 *  folder: product_sku  //entity Id
 *  files: {
 *     `sku.image1Name`: image1-Object
 *     `sku.image2Name`: image2-Object
 *
 *   }
 * }
 *
 */
export const koganProductImagesSlice = createSlice({
  name: 'koganProductImages',
  initialState,
  reducers: {
    upsertFolder: productImagesAdaptor.updateOne,
    removeFolder: productImagesAdaptor.removeOne,
  },
  extraReducers(builder) {
    builder
      .addCase(removeImage.fulfilled, (state, action) => {
        const metaImageObj = action.meta.arg;
        const folderObj = _.get(state.entities, metaImageObj.folder);
        if (folderObj?.files) {
          //remove the image meta object from the files object
          const removed = _.unset(folderObj.files, metaImageObj.uid);
          if (removed) {
            //upsert updated folderobj entity back into state
            productImagesAdaptor.upsertOne(state, folderObj);
          }
        }
      })
      .addCase(fetchImagesInFolder.fulfilled, (state, action) => {
        productImagesAdaptor.upsertOne(state, action.payload);
      })
      .addCase(addImage.fulfilled, (state, action) => {
        const metaImageObj = action.payload;
        const folderObj = _.get(state.entities, metaImageObj.folder);
        if (folderObj?.files) {
          const files = folderObj.files;
          const length = _.keys(files).length;
          metaImageObj.style = length===0?IMAGE_TYPE_ENUM.HERO.type:IMAGE_TYPE_ENUM.GALLERY.type;
          metaImageObj.sequence = length;
          _.set(files, metaImageObj.uid, metaImageObj);
        }
        productImagesAdaptor.upsertOne(state, folderObj);
      });
  },
});

//Actions
export const { upsertFolder, removeFolder } = koganProductImagesSlice.actions;
export const fetchImagesInFolder = createAsyncThunk(
  'koganProductImages/fetchImagesInFolder',
  async (product_sku) => {
    const response = await listImages(product_sku);
    const imageS3Urls = response.data.results;
    const files = {};
    imageS3Urls.forEach((s3Url, idx) => {
      const metaImageObj = createImageObj(product_sku, s3Url);
      metaImageObj.sequence = idx;
      metaImageObj.style =
        idx === 0 ? IMAGE_TYPE_ENUM.HERO : IMAGE_TYPE_ENUM.GALLERY;
      _.set(files, metaImageObj.uid, metaImageObj);
    });
    const folderObj = {
      folder: product_sku,
      files,
    };
    return folderObj;
  }
);

export const addImage = createAsyncThunk(
  'koganProductImages/addImage',
  async (metaImageObj) => {
    const response = await uploadImageToS3(
      metaImageObj
      );
      const s3url = getS3Url(metaImageObj);
      const newObj = createImageObj(metaImageObj.folder,s3url);
      newObj.status=FILE_UPLOAD_STATUS_ENUM.SUCCESS;
      return newObj;
    }
    );
    export const removeImage = createAsyncThunk(
      'koganProductImages/removeImage',
      async (metaImageObj) => {
        await deleteImageFromS3(metaImageObj);
        // no error received, so return file so it can be remved from tab.images array in the reduce
        const newObj = createImageObj(metaImageObj.folder,metaImageObj.url);
        newObj.status= FILE_UPLOAD_STATUS_ENUM.REMOVED;
        return newObj;
      }
    );
export default koganProductImagesSlice.reducer;

//selectors
export const { selectById: selectFolderById } =
  productImagesAdaptor.getSelectors((state) => state.koganProductImages);

export const getFilesInFolder = (state, sku) => {
    return selectFolderById(state, sku)?.files;
};

export const getFileUrlsInFolder = createSelector(state=>state,(state,sku)=>selectFolderById(state, sku)?.files,
(state,files)=>_.values(files).map(file=> file.url));


//Helper functions
export const createImageObj = (product_sku, imageUrl) => {
  const metaObj = {
    uid: '', // we maintain a copy of the id on the meta object itself to allow ease of access from the metaObjWrapper in the reducer
    url: imageUrl,
    folder: product_sku,
    name: getFileNameFromS3Url(imageUrl),
    style: null,
    sequence: 0,
    deleted: false,
    status:null,
    thumbUrl:'',
    percent:0,
  };
  metaObj.uid = buildMetaId(metaObj);
  return metaObj;
};

const buildMetaId = (metaObj) => {
  return `${metaObj.folder}_${metaObj.name}`.replace(/[^\w]/g, '');
};

export const getFileNameFromS3Url = (url) => {
  let lastSegment = url.substring(url.lastIndexOf('/') + 1);
  return lastSegment.replace(/\+/g, ' ');
};

export const IMAGE_TYPE_ENUM = {
  HERO: { type: 'hero' },
  GALLERY: { type: 'gallery' },
};

export const getS3Url = (file) => {
  const url = `${env.s3PublicUrl}${file.folder}/kogan/${file.name}`;

  return url.replace(/\s+/g, '+');
};

export const FILE_UPLOAD_STATUS_ENUM={
  ERROR:{type:'error'},
  SUCCESS:{type:'success'},
  DONE:{type:'done'},
  UPLOADING:{type:'uploading'},
  REMOVED:{type:'removed'},
}